import React from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Trash2, Eye, RotateCcw, Save, Upload, Download,
  Building2, User, FileText, ListChecks, Info, CreditCard,
  Receipt, ShieldCheck, Lock, Zap, ShieldAlert, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = [
  { code: "USD", label: "USD - US Dollar", symbol: "$" },
  { code: "EUR", label: "EUR - Euro", symbol: "€" },
  { code: "GBP", label: "GBP - British Pound", symbol: "£" },
  { code: "INR", label: "INR - Indian Rupee", symbol: "₹" },
  { code: "AUD", label: "AUD - Australian Dollar", symbol: "A$" },
  { code: "CAD", label: "CAD - Canadian Dollar", symbol: "C$" },
  { code: "JPY", label: "JPY - Japanese Yen", symbol: "¥" },
  { code: "AED", label: "AED - UAE Dirham", symbol: "د.إ" },
];

const LANGUAGES = ["English", "Spanish", "French", "German", "Hindi", "Arabic"];

export function currencySymbol(code) {
  return CURRENCIES.find((c) => c.code === code)?.symbol || "$";
}

export function calcTotals(invoice) {
  const rawSubtotal = invoice.items.reduce((sum, it) => {
    const line = Number(it.qty || 0) * Number(it.price || 0);
    const disc = (line * Number(it.discount || 0)) / 100;
    return sum + (line - disc);
  }, 0);
  const globalDisc = (rawSubtotal * Number(invoice.discountPct || 0)) / 100;
  const afterDisc = rawSubtotal - globalDisc;
  const tax = (afterDisc * Number(invoice.taxPct || 0)) / 100;
  const shipping = Number(invoice.shipping || 0);
  const total = afterDisc + tax + shipping;
  return { subtotal: rawSubtotal, discount: globalDisc, tax, shipping, total };
}

export default function CreateInvoice() {
  const { invoice, setInvoice, resetInvoice } = useApp();
  const navigate = useNavigate();

  const setField = (path, value) => {
    setInvoice((prev) => {
      const next = structuredClone(prev);
      let obj = next;
      const keys = path.split(".");
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys.at(-1)] = value;
      return next;
    });
  };

  const setItem = (idx, key, val) => {
    setInvoice((prev) => {
      const next = structuredClone(prev);
      next.items[idx][key] = val;
      return next;
    });
  };

  const addItem = () =>
    setInvoice((p) => ({
      ...p,
      items: [...p.items, { name: "", description: "", qty: 1, price: 0, discount: 0, tax: 0 }],
    }));
  const delItem = (idx) => setInvoice((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const uploadLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => setField("company.logo", ev.target.result);
    reader.readAsDataURL(f);
  };

  const totals = calcTotals(invoice);
  const sym = currencySymbol(invoice.meta.currency);

  const handlePreview = () => navigate("/preview");
  const handleSaveDraft = () => {
    localStorage.setItem("si-invoice", JSON.stringify(invoice));
    toast.success("Draft saved locally");
  };
  const handleClear = () => {
    if (window.confirm("Clear all invoice data?")) {
      resetInvoice();
      toast.success("Form cleared");
    }
  };
  const handleDownload = () => {
    navigate("/preview", { state: { autoDownload: true } });
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
      {/* Header row */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
            Create <span className="text-blue-600">Invoice</span>
          </h1>
          <p className="mt-2 text-muted-foreground">Fill in the details below to generate your invoice</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="save-draft-btn" onClick={handleSaveDraft} variant="outline" className="rounded-xl h-11 border-blue-200 hover:bg-blue-50 hover:text-blue-700"><Save size={15} className="mr-2" /> Save Draft</Button>
          <Button data-testid="clear-form-btn" onClick={handleClear} variant="outline" className="rounded-xl h-11 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"><RotateCcw size={15} className="mr-2" /> Clear Form</Button>
          <Button data-testid="preview-btn" onClick={handlePreview} variant="outline" className="rounded-xl h-11 border-blue-200 hover:bg-blue-50 hover:text-blue-700"><Eye size={15} className="mr-2" /> Preview</Button>
          <Button data-testid="download-btn" onClick={handleDownload} className="rounded-xl h-11 bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/25"><Download size={15} className="mr-2" /> Download PDF</Button>
        </div>
      </div>

      <div className="grid xl:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,0.85fr)] lg:grid-cols-2 gap-6">
        {/* LEFT COLUMN */}
        <div className="space-y-6">
          <SectionCard icon={Building2} title="Company Details">
            <Field label="Company Name"><Input data-testid="company-name" value={invoice.company.name} onChange={(e) => setField("company.name", e.target.value)} placeholder="Enter company name" /></Field>
            <Field label="Company Logo">
              <label className="flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-border py-6 cursor-pointer hover:bg-accent transition-colors" data-testid="company-logo-label">
                <div className="w-11 h-11 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-600 grid place-items-center"><Upload size={16} /></div>
                <div className="text-sm">
                  {invoice.company.logo ? <span className="text-emerald-600 font-medium">Logo uploaded</span> : <span className="text-foreground font-medium">Upload Logo</span>}
                </div>
                <div className="text-xs text-muted-foreground">PNG, JPG up to 2MB</div>
                <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="company-logo-input" />
              </label>
            </Field>
            <Field label="Address"><Textarea data-testid="company-address" value={invoice.company.address} onChange={(e) => setField("company.address", e.target.value)} placeholder="Enter company address" rows={2} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="GST / VAT"><Input data-testid="company-gst" value={invoice.company.gst} onChange={(e) => setField("company.gst", e.target.value)} placeholder="Enter GST or VAT number" /></Field>
              <Field label="Phone"><Input data-testid="company-phone" value={invoice.company.phone} onChange={(e) => setField("company.phone", e.target.value)} placeholder="Enter phone number" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email"><Input data-testid="company-email" type="email" value={invoice.company.email} onChange={(e) => setField("company.email", e.target.value)} placeholder="Enter email address" /></Field>
              <Field label="Website"><Input data-testid="company-website" value={invoice.company.website} onChange={(e) => setField("company.website", e.target.value)} placeholder="Enter website (optional)" /></Field>
            </div>
          </SectionCard>

          <SectionCard icon={User} title="Customer Details">
            <Field label="Customer Name"><Input data-testid="customer-name" value={invoice.customer.name} onChange={(e) => setField("customer.name", e.target.value)} placeholder="Enter customer name" /></Field>
            <Field label="Address"><Textarea data-testid="customer-address" value={invoice.customer.address} onChange={(e) => setField("customer.address", e.target.value)} rows={2} placeholder="Enter customer address" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone"><Input data-testid="customer-phone" value={invoice.customer.phone} onChange={(e) => setField("customer.phone", e.target.value)} placeholder="Enter phone number" /></Field>
              <Field label="Email"><Input data-testid="customer-email" type="email" value={invoice.customer.email} onChange={(e) => setField("customer.email", e.target.value)} placeholder="Enter email address" /></Field>
            </div>
          </SectionCard>
        </div>

        {/* MIDDLE COLUMN */}
        <div className="space-y-6">
          <SectionCard icon={FileText} title="Invoice Details">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Invoice Number"><Input data-testid="invoice-number" value={invoice.meta.number} onChange={(e) => setField("meta.number", e.target.value)} className="bg-blue-50/60 dark:bg-blue-950/20 border-blue-100 dark:border-blue-900" /></Field>
              <Field label="Invoice Date"><Input data-testid="invoice-date" type="date" value={invoice.meta.date} onChange={(e) => setField("meta.date", e.target.value)} /></Field>
              <Field label="Due Date"><Input data-testid="invoice-due-date" type="date" value={invoice.meta.dueDate} onChange={(e) => setField("meta.dueDate", e.target.value)} /></Field>
              <Field label="Currency">
                <Select value={invoice.meta.currency} onValueChange={(v) => setField("meta.currency", v)}>
                  <SelectTrigger data-testid="invoice-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Language">
              <Select value={invoice.meta.language} onValueChange={(v) => setField("meta.language", v)}>
                <SelectTrigger data-testid="invoice-language"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l} value={l.toLowerCase().slice(0, 2)}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </SectionCard>

          <SectionCard icon={ListChecks} title="Items">
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-xs min-w-[560px]">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-2 pr-2 font-medium w-6">#</th>
                    <th className="py-2 pr-2 font-medium">Item Name</th>
                    <th className="py-2 pr-2 font-medium">Description</th>
                    <th className="py-2 pr-2 font-medium w-14">Qty</th>
                    <th className="py-2 pr-2 font-medium w-20">Price</th>
                    <th className="py-2 pr-2 font-medium w-16">Discount</th>
                    <th className="py-2 pr-2 font-medium w-16">Tax (%)</th>
                    <th className="py-2 pr-2 font-medium text-right w-20">Total</th>
                    <th className="w-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((it, idx) => {
                    const line = Number(it.qty || 0) * Number(it.price || 0);
                    const lineDisc = (line * Number(it.discount || 0)) / 100;
                    const lineTax = ((line - lineDisc) * Number(it.tax || 0)) / 100;
                    const lineTotal = line - lineDisc + lineTax;
                    return (
                      <tr key={idx} className="border-b border-border/60">
                        <td className="py-2 pr-2 text-muted-foreground">{idx + 1}</td>
                        <td className="py-2 pr-2"><Input data-testid={`item-name-${idx}`} value={it.name} onChange={(e) => setItem(idx, "name", e.target.value)} className="h-8 text-xs" /></td>
                        <td className="py-2 pr-2"><Input data-testid={`item-desc-${idx}`} value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} className="h-8 text-xs" /></td>
                        <td className="py-2 pr-2"><Input data-testid={`item-qty-${idx}`} type="number" min="0" value={it.qty} onChange={(e) => setItem(idx, "qty", e.target.value)} className="h-8 text-xs px-2" /></td>
                        <td className="py-2 pr-2"><Input data-testid={`item-price-${idx}`} type="number" min="0" step="0.01" value={it.price} onChange={(e) => setItem(idx, "price", e.target.value)} className="h-8 text-xs px-2" /></td>
                        <td className="py-2 pr-2"><Input data-testid={`item-disc-${idx}`} type="number" min="0" max="100" value={it.discount} onChange={(e) => setItem(idx, "discount", e.target.value)} className="h-8 text-xs px-2" /></td>
                        <td className="py-2 pr-2"><Input data-testid={`item-tax-${idx}`} type="number" min="0" max="100" value={it.tax} onChange={(e) => setItem(idx, "tax", e.target.value)} className="h-8 text-xs px-2" /></td>
                        <td className="py-2 pr-2 text-right font-medium tabular-nums">{sym}{lineTotal.toFixed(2)}</td>
                        <td>
                          {invoice.items.length > 1 && (
                            <button data-testid={`item-del-${idx}`} onClick={() => delItem(idx)} className="text-red-500 hover:opacity-70 transition-opacity p-1">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Button data-testid="add-item-btn" onClick={addItem} variant="outline" className="mt-4 rounded-xl border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800 w-full">
              <Plus size={14} className="mr-1" /> Add New Item
            </Button>
          </SectionCard>

          <div className="grid sm:grid-cols-2 gap-6">
            <SectionCard icon={Info} title="Additional Information" compact>
              <Field label="Notes (Optional)"><Textarea data-testid="notes" rows={3} value={invoice.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="Add any notes or payment instructions..." /></Field>
              <Field label="Terms & Conditions (Optional)"><Textarea data-testid="terms" rows={3} value={invoice.terms} onChange={(e) => setField("terms", e.target.value)} placeholder="Add terms & conditions..." /></Field>
            </SectionCard>

            <SectionCard icon={CreditCard} title="Payment Methods" compact>
              {[
                ["upi", "UPI / QR Code"], ["bank", "Bank Transfer"], ["cash", "Cash"],
                ["card", "Card"], ["paypal", "PayPal"],
              ].map(([k, l]) => (
                <label key={k} className="flex items-center gap-3 py-1.5 cursor-pointer">
                  <Checkbox data-testid={`pay-${k}`} checked={invoice.payments[k]} onCheckedChange={(v) => setField(`payments.${k}`, !!v)} />
                  <span className="text-sm">{l}</span>
                </label>
              ))}
            </SectionCard>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-6">
          <div className="sticky top-20 space-y-6">
            <SectionCard icon={Receipt} title="Invoice Summary">
              <SummaryRow label="Subtotal" value={`${sym}${totals.subtotal.toFixed(2)}`} />
              <div className="flex items-center justify-between py-2 border-b border-border/60">
                <span className="text-sm text-muted-foreground">Discount</span>
                <div className="flex items-center gap-2">
                  <Input data-testid="global-discount" type="number" min="0" max="100" value={invoice.discountPct} onChange={(e) => setField("discountPct", e.target.value)} className="w-16 h-8 text-right text-xs" />
                  <span className="text-xs text-muted-foreground w-3">%</span>
                  <span className="text-sm tabular-nums w-16 text-right">-{sym}{totals.discount.toFixed(2)}</span>
                </div>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-border/60">
                <span className="text-sm text-muted-foreground">Tax</span>
                <div className="flex items-center gap-2">
                  <Input data-testid="global-tax" type="number" min="0" max="100" value={invoice.taxPct} onChange={(e) => setField("taxPct", e.target.value)} className="w-16 h-8 text-right text-xs" />
                  <span className="text-xs text-muted-foreground w-3">%</span>
                  <span className="text-sm tabular-nums w-16 text-right">{sym}{totals.tax.toFixed(2)}</span>
                </div>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-border/60">
                <span className="text-sm text-muted-foreground">Shipping</span>
                <div className="flex items-center gap-2">
                  <Input data-testid="shipping" type="number" min="0" value={invoice.shipping} onChange={(e) => setField("shipping", e.target.value)} className="w-20 h-8 text-right text-xs" />
                  <span className="text-sm tabular-nums w-16 text-right">{sym}{totals.shipping.toFixed(2)}</span>
                </div>
              </div>
              <div className="flex items-center justify-between pt-4">
                <span className="font-heading font-semibold">Total Amount</span>
                <span className="font-heading font-bold text-2xl text-blue-600 tabular-nums">{sym}{totals.total.toFixed(2)}</span>
              </div>
              <Button data-testid="summary-preview-btn" onClick={handlePreview} className="w-full mt-5 rounded-xl h-12 bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-500/25">
                <Eye size={15} className="mr-2" /> Preview Invoice
              </Button>
              <div className="mt-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 text-xs px-3 py-2 flex items-center gap-2">
                <Sparkles size={13} /> Live preview will update automatically
              </div>
            </SectionCard>

            <div className="rounded-2xl bg-blue-50/60 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/40 p-6">
              <div className="font-heading font-bold text-center mb-5">Why Choose Simple Invoice?</div>
              <ul className="space-y-4 text-sm">
                {[
                  [ShieldCheck, "100% Free Forever", "No hidden charges, completely free"],
                  [Lock, "No Login Required", "Create and download instantly"],
                  [ShieldAlert, "Secure & Private", "Your data stays on your device"],
                  [Receipt, "Professional Templates", "Beautiful invoices that impress"],
                ].map(([Icon, t, d]) => (
                  <li key={t} className="flex items-start gap-3">
                    <span className="shrink-0 w-9 h-9 rounded-full bg-white dark:bg-slate-900 text-blue-600 grid place-items-center shadow-sm">
                      <Icon size={15} />
                    </span>
                    <div>
                      <div className="font-semibold">{t}</div>
                      <div className="text-xs text-muted-foreground">{d}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom trust banner */}
      <div className="mt-10 rounded-2xl bg-blue-50/60 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/40 p-5 flex flex-wrap items-center justify-between gap-6">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-xl bg-blue-600 text-white grid place-items-center shrink-0"><ShieldCheck size={18} /></span>
          <div className="text-sm">
            <div className="font-semibold">No login required. Your data stays on your device only.</div>
            <div className="text-xs text-muted-foreground">We don&apos;t store any of your information on our servers.</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          <TrustBadge icon={Lock} label="Secure" sub="256-bit SSL" />
          <TrustBadge icon={ShieldAlert} label="Private" sub="No data stored" />
          <TrustBadge icon={Zap} label="Fast" sub="Instant download" />
        </div>
      </div>
    </div>
  );
}

function SectionCard({ icon: Icon, title, children, compact }) {
  return (
    <section className={`rounded-2xl bg-card border border-border ${compact ? "p-5" : "p-6"} shadow-[0_8px_30px_rgb(0,0,0,0.03)] space-y-4`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 grid place-items-center">
          <Icon size={16} />
        </span>
        <h2 className="font-heading font-bold">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/60">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm tabular-nums font-medium">{value}</span>
    </div>
  );
}

function TrustBadge({ icon: Icon, label, sub }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 grid place-items-center">
        <Icon size={15} />
      </span>
      <div>
        <div className="text-xs font-semibold">{label}</div>
        <div className="text-[10px] text-muted-foreground">{sub}</div>
      </div>
    </div>
  );
}
